// ── Suppress Chrome/Firefox extension messaging noise ─────────────────────
// Runs immediately at module load (before any React render) so errors from
// extension background scripts never reach the console.
const EXTENSION_ERROR_PATTERNS = [
  'FrameDoesNotExistError',
  'Could not establish connection',
  'Receiving end does not exist',
  'chrome-extension://',
  'moz-extension://',
  'Frame ',
  'background.js',
  // Extension messaging noise — emitted by content scripts whose port closed,
  // whose host page bf-cached, or which lack host permission for the tab.
  'runtime.lastError',
  'The message port closed before a response was received',
  'message channel is closed',
  'back/forward cache',
  'Cannot access contents of the page',
  'Extension manifest must request permission',
];

export const isExtensionNoise = (msg: string | undefined): boolean =>
  !!msg && EXTENSION_ERROR_PATTERNS.some(p => msg.includes(p));

export const isExtensionNoiseArgs = (args: unknown[]): boolean =>
  args.some((arg) => {
    if (arg instanceof Error) {
      return isExtensionNoise(arg.message) || isExtensionNoise(arg.stack);
    }
    return isExtensionNoise(String(arg ?? ''));
  });

const BENIGN_PDF_NOISE_PATTERNS: RegExp[] = [
  /tt:\s*undefined function/i,
  /warning:\s*tt:/i,
  /warning:\s*gethexstring/i,
  /warning:\s*indexing all pdf/i,
  /warning:\s*jpx numcomps/i,
  /warning:\s*invalid marked content and clip nesting/i,
  /warning:\s*\.\.\.\s*repeated\s+\d+\s+times/i,
  // pdf.js / mupdf throws these when a user uploads an unparseable PDF.
  // Not a code bug — the loader is correctly refusing the file. Was
  // flooding the support inbox with 5+ identical tickets per session.
  /InvalidPDFException/i,
  /Invalid PDF structure/i,
  // The Autodesk viewer asks every model for a property database. A PDF has
  // no properties, so it logs this on every single sheet open — through
  // console.error, which puts a routine, expected condition in front of the
  // user as though something had failed.
  /Missing property database entry in manifest/i,
  /No "GlobalWorkerOptions\.workerSrc" specified/i,
];

export const isBenignPdfNoise = (msg: string | undefined): boolean =>
  !!msg && BENIGN_PDF_NOISE_PATTERNS.some((pattern) => pattern.test(msg));

export const isBenignPdfNoiseArgs = (args: unknown[]): boolean =>
  args.some((arg) => {
    if (arg instanceof Error) {
      return isBenignPdfNoise(arg.message) || isBenignPdfNoise(arg.stack);
    }
    return isBenignPdfNoise(String(arg ?? ''));
  });

// Harmless browser-engine quirks that browsers themselves elevate to error
// events but that have no user-visible effect. Filed as bug tickets twice
// today before being marked ignored — pre-empt the cycle.
const BENIGN_BROWSER_NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver loop limit exceeded/i,
];

export const isBenignBrowserNoise = (msg: string | undefined): boolean =>
  !!msg && BENIGN_BROWSER_NOISE_PATTERNS.some((pattern) => pattern.test(msg));

export const isBenignBrowserNoiseArgs = (args: unknown[]): boolean =>
  args.some((arg) => {
    if (arg instanceof Error) {
      return isBenignBrowserNoise(arg.message) || isBenignBrowserNoise(arg.stack);
    }
    return isBenignBrowserNoise(String(arg ?? ''));
  });

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message ?? String(event.reason ?? '');
  if (isExtensionNoise(msg)) event.preventDefault();
});

window.addEventListener('error', (event) => {
  if (isExtensionNoise(event.message) || isExtensionNoise(event.filename)) {
    event.preventDefault();
  }
}, true);

/**
 * Suppress canvas CORS errors
 * 
 * This error occurs when browser extensions or dev tools try to capture
 * canvas content that contains images from external domains.
 * 
 * Common causes:
 * - React DevTools taking screenshots
 * - Browser extensions (Grammarly, LastPass, etc.)
 * - PDF generators
 * - Screenshot tools
 * 
 * The error is harmless and doesn't affect functionality.
 */

// Idempotence guard: the prototype patches below must be applied at most once.
// suppressCanvasErrors() runs from an App effect, and a remount (StrictMode,
// auth boundary) would otherwise stack a second wrapper over the first.
let canvasErrorsSuppressed = false;

export function suppressCanvasErrors() {
  if (canvasErrorsSuppressed) return;
  canvasErrorsSuppressed = true;

  // Override HTMLCanvasElement.prototype.toDataURL
  const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;
  
  HTMLCanvasElement.prototype.toDataURL = function(...args) {
    try {
      return originalToDataURL.apply(this, args);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // Silently return empty data URL for tainted canvases
        // (occurs when browser extensions try to capture canvas content)
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
      }
      throw error;
    }
  };

  // Override HTMLCanvasElement.prototype.toBlob
  const originalToBlob = HTMLCanvasElement.prototype.toBlob;
  
  HTMLCanvasElement.prototype.toBlob = function(callback, ...args) {
    try {
      return originalToBlob.call(this, callback, ...args);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // Silently handle canvas CORS error (occurs with browser extensions)
        // Call callback with null to indicate failure
        if (callback) {
          callback(null);
        }
        return;
      }
      throw error;
    }
  };

  // NOTE: there is deliberately NO getContext/drawImage patch here. An earlier
  // version wrapped ctx.drawImage on every getContext('2d') call — but the
  // browser returns the SAME context object per canvas, so each call stacked
  // another wrapper whose "original" was the previous wrapper. Long takeoff
  // sessions re-blit the same on-screen canvas hundreds of times (adaptive
  // re-rasters on every pan/zoom settle), and the chain eventually made every
  // drawImage recurse to "Maximum call stack size exceeded" — the raster
  // failed and the drawing disappeared (issues #782/#783/#795). The wrapper
  // was also a functional no-op (catch-and-rethrow), so nothing is lost.
}

export const toSafeConsoleArg = (arg: unknown): unknown => {
  if (arg == null) return arg;
  if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean' || typeof arg === 'bigint') {
    return arg;
  }
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof Event !== 'undefined' && arg instanceof Event) return `[Event ${arg.type}]`;
  if (typeof arg === 'object') {
    const ctor = (arg as { constructor?: { name?: string } })?.constructor?.name || 'Object';
    return `[${ctor}]`;
  }
  return String(arg);
};

export const forwardConsoleSafely = (logFn: (...args: any[]) => void, args: any[]): void => {
  try {
    logFn.apply(console, args);
  } catch {
    try {
      logFn.apply(console, args.map(toSafeConsoleArg));
    } catch {
      // Ignore logger failures to avoid breaking app control flow
    }
  }
};
